import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudUpload, CloudDownload, ShieldCheck, Loader2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { encryptBackup, decryptBackup, restoreLocalData, hasLocalData } from "@/lib/backup";

function when(iso) {
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export const BackupRestore = () => {
  const [meta, setMeta] = useState(null); // { exists, updatedAt } | null (loading)
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [restorePin, setRestorePin] = useState("");
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const loadMeta = () => {
    api.backupGet()
      .then((r) => setMeta({ exists: !!r.exists, updatedAt: r.updatedAt || null }))
      .catch(() => setMeta({ exists: false, updatedAt: null }));
  };
  useEffect(() => { loadMeta(); }, []);

  const doBackup = async () => {
    if (pin.length < 6) { toast.error("PIN minimal 6 karakter."); return; }
    if (pin !== pin2) { toast.error("Konfirmasi PIN tidak sama."); return; }
    if (!hasLocalData()) { toast.error("Belum ada data untuk dicadangkan."); return; }
    setBusy(true);
    try {
      const payload = await encryptBackup(pin);
      const r = await api.backupSave(payload);
      setMeta({ exists: true, updatedAt: r.updatedAt || new Date().toISOString() });
      setPin(""); setPin2("");
      toast.success("Data berhasil dicadangkan (terenkripsi).");
    } catch (e) {
      toast.error(e.message || "Gagal mencadangkan data.");
    } finally { setBusy(false); }
  };

  const doRestore = async () => {
    if (restorePin.length < 6) { toast.error("Masukkan PIN cadangan Anda."); return; }
    setRestoring(true);
    try {
      const r = await api.backupGet();
      if (!r.exists) { toast.error("Belum ada cadangan di akun ini."); setRestoring(false); return; }
      if (!window.confirm("Pulihkan data cadangan? Data di perangkat ini akan ditimpa oleh data cadangan.")) {
        setRestoring(false);
        return;
      }
      let obj;
      try {
        obj = await decryptBackup({ blob: r.blob, salt: r.salt, iv: r.iv }, restorePin);
      } catch {
        toast.error("PIN salah atau data cadangan rusak.");
        setRestoring(false);
        return;
      }
      restoreLocalData(obj);
      toast.success("Data dipulihkan. Memuat ulang…");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e.message || "Gagal memulihkan data.");
      setRestoring(false);
    }
  };

  return (
    <div className="mb-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="backup-restore-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-600" />
        <p className="font-heading text-sm font-bold text-zinc-900">Cadangkan &amp; Pulihkan (Lintas Perangkat)</p>
      </div>
      <p className="mt-1.5 text-xs leading-relaxed text-zinc-500">
        Pindah HP tanpa mengulang isian. Data dienkripsi di perangkat dengan PIN Anda
        sebelum diunggah — <b>server kami tidak bisa membaca angka keuangan Anda</b>.
      </p>

      {meta === null ? (
        <p className="mt-3 text-xs text-zinc-400">Memuat status cadangan…</p>
      ) : meta.exists ? (
        <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid="backup-status">
          Terakhir dicadangkan: <b>{meta.updatedAt ? when(meta.updatedAt) : "—"}</b>
        </p>
      ) : (
        <p className="mt-3 rounded-xl bg-zinc-50 px-3 py-2 text-xs text-zinc-500" data-testid="backup-status">
          Belum ada cadangan di akun ini.
        </p>
      )}

      {/* Backup */}
      <div className="mt-4 border-t border-zinc-100 pt-4">
        <div className="mb-2 flex items-center gap-2">
          <CloudUpload className="h-4 w-4 text-zinc-700" />
          <p className="text-sm font-semibold text-zinc-800">Cadangkan sekarang</p>
        </div>
        <input
          data-testid="backup-pin"
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="Buat PIN (min. 6 karakter)"
          className="mb-2 h-11 w-full rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
        <input
          data-testid="backup-pin-confirm"
          type="password"
          value={pin2}
          onChange={(e) => setPin2(e.target.value)}
          placeholder="Ulangi PIN"
          className="mb-3 h-11 w-full rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
        <button
          data-testid="backup-btn"
          onClick={doBackup}
          disabled={busy}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}
          {busy ? "Mencadangkan…" : "Cadangkan (Terenkripsi)"}
        </button>
      </div>

      {/* Restore */}
      <div className="mt-4 border-t border-zinc-100 pt-4">
        <div className="mb-2 flex items-center gap-2">
          <CloudDownload className="h-4 w-4 text-zinc-700" />
          <p className="text-sm font-semibold text-zinc-800">Pulihkan di perangkat ini</p>
        </div>
        <input
          data-testid="restore-pin"
          type="password"
          value={restorePin}
          onChange={(e) => setRestorePin(e.target.value)}
          placeholder="Masukkan PIN cadangan Anda"
          className="mb-3 h-11 w-full rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />
        <button
          data-testid="restore-btn"
          onClick={doRestore}
          disabled={restoring || (meta && !meta.exists)}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
        >
          {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudDownload className="h-4 w-4" />}
          {restoring ? "Memulihkan…" : "Pulihkan Data"}
        </button>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2">
        <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
        <p className="text-[11px] leading-relaxed text-amber-700">
          Ingat PIN Anda baik-baik. Karena data dienkripsi di perangkat, <b>PIN yang hilang
          berarti cadangan tidak dapat dipulihkan</b> — kami tidak menyimpan PIN dan tidak bisa meresetnya.
        </p>
      </div>
    </div>
  );
};
